/**
 * MapShell: owns the MapLibre GL JS instance for the district-drawing map.
 *
 * What it does TODAY:
 *  - Registers the pmtiles:// protocol (idempotent across remounts).
 *  - Adds a `blocks` vector source pointing at the tenant's PMTiles archive
 *    ({VITE_TILES_BASE_URL}/{dataset_hash}/blocks.pmtiles or the tenant's
 *    explicit map.tiles_url), with `promoteId` set to the stable block id
 *    property (e.g. GEOID) so runtime feature-state can address blocks.
 *  - Renders a fill layer whose color expression reads feature-state
 *    `district` → district color. The painting tool
 *    (src/map/tools/painting.ts) writes feature-state through the
 *    `onMapReady` hook; the reference overlays
 *    (src/map/overlays.ts) add their own layers above this one.
 *
 * What is OUT OF SCOPE (later passes): lasso, flood fill, the accessible
 * block-list editor. See the PAINT HOOKS section below for the exact
 * integration points — `onMapReady` exposes the live map to them.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { TenantConfig } from '../lib/api';
import { resolveDistrictColors } from '../lib/districtColors';
import { registerPmtilesProtocol, resolveBlocksTilesUrl } from '../lib/pmtiles';
import { ensureBasemap } from './basemap';
import { GLYPHS_URL, registerLocalGlyphs } from './glyphs';
import { useBasemapStore } from '../stores/basemap';

/** Source id for the census-block vector source. */
export const BLOCKS_SOURCE_ID = 'blocks';
/** Fill layer id driven by feature-state district assignment. */
export const BLOCKS_FILL_LAYER_ID = 'blocks-fill';
/** Outline layer id (jurisdiction/blocks boundary readability). */
export const BLOCKS_OUTLINE_LAYER_ID = 'blocks-outline';
/** Brush-preview outline: blocks with feature-state `hover`. */
export const BLOCKS_HOVER_LAYER_ID = 'blocks-hover';
/** Default opacity of assigned district fills (user-adjustable). */
export const DEFAULT_DISTRICT_FILL_OPACITY = 0.62;
/**
 * County-scale tenants fit their full bounds around zoom 9–10, where
 * census-block outlines are almost invisible. Open no farther out than this
 * so the first view shows block lines.
 */
export const INITIAL_MIN_ZOOM = 12;

const ASSIGNED = ['!=', ['coalesce', ['feature-state', 'district'], -1], -1];

/**
 * Fill opacity: assigned blocks at `base`, unassigned blocks a faint wash so
 * the jurisdiction stays legible, hovered blocks lifted so the brush preview
 * reads clearly.
 */
export function buildFillOpacity(base: number): maplibregl.DataDrivenPropertyValueSpecification<number> {
  return [
    'case',
    ['boolean', ['feature-state', 'hover'], false],
    Math.min(1, base + 0.18),
    ASSIGNED,
    base,
    0.07,
  ] as unknown as maplibregl.DataDrivenPropertyValueSpecification<number>;
}

/** Change the district fill opacity on a live map. */
export function setDistrictFillOpacity(map: maplibregl.Map, base: number): void {
  if (map.getLayer(BLOCKS_FILL_LAYER_ID)) {
    map.setPaintProperty(BLOCKS_FILL_LAYER_ID, 'fill-opacity', buildFillOpacity(base));
  }
}

/**
 * Build the fill-color expression for the blocks layer.
 *
 * Reads `['feature-state', 'district']` (a district index) and maps it to
 * the resolved district colors. Blocks with no assignment render a faint
 * neutral fill so the base geography stays legible.
 *
 * Colors come from `resolveDistrictColors`, the same resolver the palette
 * and stats table use — so the map, the legend, and the store can never
 * disagree, even when the tenant config supplies fewer colors than
 * districts.
 */
function buildDistrictFillExpression(
  districtCount: number,
  colors: readonly string[] | undefined,
): maplibregl.DataDrivenPropertyValueSpecification<string> {
  return fillExpressionFor(resolveDistrictColors(districtCount, colors));
}

function fillExpressionFor(
  resolved: readonly string[],
): maplibregl.DataDrivenPropertyValueSpecification<string> {
  const unassigned = '#78716c';
  if (resolved.length === 0) {
    return unassigned as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>;
  }
  const cases: Array<string | number> = [];
  resolved.forEach((color, i) => cases.push(i, color));
  return ['match', ['feature-state', 'district'], ...cases, unassigned] as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>;
}

/**
 * Point the live map's district fill at the open plan's own colors. The
 * style starts from the jurisdiction's default district count; a plan can
 * have more districts than that, and without this those extra districts
 * would render in the "unassigned" grey.
 */
export function setDistrictFillColors(map: maplibregl.Map, colors: readonly string[]): void {
  if (map.getLayer(BLOCKS_FILL_LAYER_ID)) {
    map.setPaintProperty(BLOCKS_FILL_LAYER_ID, 'fill-color', fillExpressionFor(colors));
  }
}

function buildStyle(tenant: TenantConfig, tilesUrl: string): maplibregl.StyleSpecification {
  const sourceLayer = tenant.map.source_layer;
  const idProperty = tenant.map.id_property;

  return {
    version: 8,
    name: `${tenant.slug}-blocks`,
    // Label glyphs are drawn in the browser (map/glyphs.ts).
    glyphs: GLYPHS_URL,
    sources: {
      [BLOCKS_SOURCE_ID]: {
        type: 'vector',
        url: `pmtiles://${tilesUrl}`,
        // Stable block id (e.g. GEOID) promoted so feature-state can target
        // individual blocks by id instead of fragile generated indices.
        promoteId: { [sourceLayer]: idProperty },
      },
    },
    layers: [
      {
        id: BLOCKS_FILL_LAYER_ID,
        type: 'fill',
        source: BLOCKS_SOURCE_ID,
        'source-layer': sourceLayer,
        paint: {
          'fill-color': buildDistrictFillExpression(
            tenant.districts.count,
            tenant.districts.colors,
          ),
          'fill-opacity': buildFillOpacity(DEFAULT_DISTRICT_FILL_OPACITY),
        },
      },
      {
        // Block edges: soft white inside districts (reads as texture, not
        // noise), warm grey on unassigned ground.
        id: BLOCKS_OUTLINE_LAYER_ID,
        type: 'line',
        source: BLOCKS_SOURCE_ID,
        'source-layer': sourceLayer,
        paint: {
          'line-color': [
            'case',
            ASSIGNED,
            'rgba(255, 255, 255, 0.55)',
            'rgba(68, 64, 60, 0.28)',
          ] as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>,
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.35, 12, 0.85, 15, 1.25],
        },
      },
      {
        id: BLOCKS_HOVER_LAYER_ID,
        type: 'line',
        source: BLOCKS_SOURCE_ID,
        'source-layer': sourceLayer,
        paint: {
          'line-color': '#1C2A33',
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 14, 2.2],
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            0.9,
            0,
          ] as unknown as maplibregl.DataDrivenPropertyValueSpecification<number>,
        },
      },
    ],
  };
}

export interface MapShellProps {
  tenant: TenantConfig;
  className?: string;
  /**
   * Escape hatch for the drawing pass: receives the live MapLibre map after
   * the style loads so paint tools can attach listeners and write
   * feature-state without touching MapShell internals.
   *
   * Ignored when `editable` is false.
   */
  onMapReady?: (map: maplibregl.Map) => void;
  /**
   * When false, the map is view-only: `onMapReady` is never called, so paint
   * tools cannot attach even if passed. Pan/zoom still work. Defaults to
   * true. Used by the public shared-plan view.
   */
  editable?: boolean;
  /**
   * Assignments rendered as feature-state once the style loads, keyed by the
   * promoted block id (e.g. GEOID) with 0-based district indices. Used by
   * read-only views that don't go through the painting store.
   */
  initialAssignments?: Record<string, number>;
}

export function MapShell(props: MapShellProps): JSX.Element {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const onMapReadyRef = useRef(props.onMapReady);
  onMapReadyRef.current = props.onMapReady;
  const editableRef = useRef(props.editable !== false);
  editableRef.current = props.editable !== false;
  const initialAssignmentsRef = useRef(props.initialAssignments);
  initialAssignmentsRef.current = props.initialAssignments;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let tilesUrl: string;
    try {
      registerPmtilesProtocol();
      tilesUrl = resolveBlocksTilesUrl({
        tilesUrl: props.tenant.map.tiles_url,
        datasetHash: props.tenant.map.dataset_hash,
      });
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Tile source misconfigured.');
      return;
    }

    registerLocalGlyphs();
    const map = new maplibregl.Map({
      container,
      style: buildStyle(props.tenant, tilesUrl),
      // Constrain the view to the jurisdiction when the config provides it.
      ...(props.tenant.map.bounds
        ? { bounds: props.tenant.map.bounds, fitBoundsOptions: { padding: 24 } }
        : {}),
      ...(props.tenant.map.center
        ? { center: props.tenant.map.center, zoom: INITIAL_MIN_ZOOM }
        : { center: [-122.27, 37.8], zoom: INITIAL_MIN_ZOOM }),
      minZoom: props.tenant.map.min_zoom ?? 7,
      maxZoom: props.tenant.map.max_zoom ?? 16,
      attributionControl: { compact: true },
    });
    // fitBounds (used when the tenant has jurisdiction bounds) can land
    // below INITIAL_MIN_ZOOM on a county-sized box. Nudge in so block
    // outlines are readable on first paint. Smaller demo grids already
    // fit past this and are left alone.
    if (map.getZoom() < INITIAL_MIN_ZOOM) {
      map.setZoom(INITIAL_MIN_ZOOM);
    }
    mapRef.current = map;
    // Dev-only test hook: lets end-to-end tests aim at real blocks.
    if (import.meta.env.DEV) (window as unknown as { __rdMap?: maplibregl.Map }).__rdMap = map;

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 100, unit: 'imperial' }), 'bottom-right');

    // Basemap preference: apply on load, then follow the store so the
    // layer-toggles UI switches it instantly without a style rebuild.
    // Gate on the style's initial load only. `isStyleLoaded()` reports false
    // whenever ANY source is mid-fetch — with several overlay sources on this
    // map that is close to permanent, which would silently drop every switch.
    let styleReady = false;
    const unsubscribeBasemap = useBasemapStore.subscribe((state) => {
      const live = mapRef.current;
      if (live && styleReady) ensureBasemap(live, state.choice);
    });

    const handleError = (event: ErrorEvent): void => {
      // Tile/network errors are non-fatal; surface them instead of a dead map.
      setLoadError((prev) => prev ?? event.message);
    };
    map.on('error', handleError);

    map.on('load', () => {
      styleReady = true;
      ensureBasemap(map, useBasemapStore.getState().choice);
      // Render caller-supplied assignments (read-only views). Uses the same
      // feature-state contract the paint tools use.
      const initial = initialAssignmentsRef.current;
      if (initial) {
        const sourceLayer = props.tenant.map.source_layer;
        for (const [geoid, district] of Object.entries(initial)) {
          map.setFeatureState(
            { source: BLOCKS_SOURCE_ID, sourceLayer, id: geoid },
            { district },
          );
        }
      }
      // ── PAINT HOOKS (painting pass: implemented) ─────────────────────
      // The supported contract, used by src/map/tools/painting.ts:
      //   1. Write assignments with feature-state, keyed by the promoted id:
      //        map.setFeatureState(
      //          { source: BLOCKS_SOURCE_ID, sourceLayer: '<tenant source-layer>', id: '<GEOID>' },
      //          { district: <district index> },
      //        );
      //   2. Clear with map.removeFeatureState({ source: BLOCKS_SOURCE_ID, sourceLayer }).
      //   3. BLOCKS_FILL_LAYER_ID recolors from feature-state automatically —
      //      no layer edits needed for paint feedback (<50ms target path).
      //   4. Tools live in src/map/tools/* and receive the map via
      //      onMapReady; keep pointer math off the React render path.
      // View-only mode (editable={false}): tools are never attached, even
      // if a caller passed onMapReady.
      if (editableRef.current) {
        onMapReadyRef.current?.(map);
      }
    });

    // MapLibre tracks window resizes, not container resizes: panels
    // collapsing, full-map mode, and bottom sheets all resize the container.
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => map.resize()) : null;
    resizeObserver?.observe(container);

    return () => {
      resizeObserver?.disconnect();
      unsubscribeBasemap();
      map.off('error', handleError);
      map.remove();
      mapRef.current = null;
    };
  }, [props.tenant]);

  return (
    <div className={`maplibre-shell relative ${props.className ?? ''}`} ref={containerRef}>
      {loadError && (
        <div
          role="alert"
          className="absolute inset-x-0 top-0 z-10 m-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <p className="font-medium">{t('map.tilesFailed')}</p>
          <p className="mt-1 text-amber-800">{loadError}</p>
        </div>
      )}
      {/* Screen-reader / non-map path note. The accessible block-list editor
          is a later gate; this keeps the region labeled for now. */}
      <span className="sr-only">
        {props.editable !== false ? t('map.srEditable') : t('map.srReadonly')}
      </span>
    </div>
  );
}
